import { useState, useMemo, useCallback, useRef, useEffect, memo } from 'react';
import {
  makeStyles,
  mergeClasses,
  tokens,
  Button,
  Tooltip,
  Input,
} from '@fluentui/react-components';
import {
  CopyFilled,
  CheckmarkFilled,
  ArrowDownloadRegular,
  SearchRegular,
  DismissRegular,
} from '@fluentui/react-icons';
import { customScrollbarStyles } from '@/styles/scrollbar';
import { countMatchesInData, highlightText } from '@/utils/json';

type JsonViewerProps = {
  data: unknown;
  label?: string;
  /** Filename stem for download (".json" appended). Defaults to "data". */
  downloadName?: string;
  /** Override default max height (e.g., for use inside a tall dialog pane). */
  maxHeight?: string;
};

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    minHeight: 0,
    flex: 1,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalS,
  },
  label: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    flexWrap: 'nowrap',
    width: '100%',
    minWidth: 0,
  },
  // Right-aligned cluster inside the toolbar (Formatted/Raw + copy +
  // download). `marginLeft: auto` pushes this group to the trailing edge
  // regardless of how the search input flex-sizes itself — Fluent's
  // <Input> root uses inline-flex with its own width logic, so a plain
  // `justify-content: space-between` doesn't reliably separate the two.
  toolbarRight: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    flexShrink: 0,
    marginLeft: 'auto',
  },
  searchInput: {
    // Pin both width and height so the input keeps a stable size whether
    // contentAfter (counter + clear button) is present or not. Without
    // pinning, the input grew on first keystroke (the small Button in
    // contentAfter pushed both width and height) and shoved the JSON
    // viewer below it, creating a layout jitter. flexShrink: 1 +
    // minWidth: 0 still allows graceful compression in narrow toolbars.
    width: '240px',
    maxWidth: '240px',
    height: '24px',
    flexShrink: 1,
    minWidth: 0,
  },
  segGroup: {
    display: 'inline-flex',
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    overflow: 'hidden',
  },
  segBtn: {
    border: 0,
    backgroundColor: 'transparent',
    color: tokens.colorNeutralForeground2,
    paddingTop: tokens.spacingVerticalXS,
    paddingBottom: tokens.spacingVerticalXS,
    paddingLeft: tokens.spacingHorizontalS,
    paddingRight: tokens.spacingHorizontalS,
    fontSize: tokens.fontSizeBase200,
    cursor: 'pointer',
    transitionProperty: 'background-color, color',
    transitionDuration: '100ms',
    ':hover': { backgroundColor: tokens.colorNeutralBackground3Hover },
    ':focus-visible': { outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: '-2px' },
  },
  segBtnActive: {
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground1,
    fontWeight: tokens.fontWeightSemibold,
  },
  jsonContainer: {
    position: 'relative',
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
    padding: tokens.spacingVerticalM,
    overflow: 'auto',
    maxHeight: '300px',
    minHeight: 0,
    flex: 1,
    ...customScrollbarStyles,
  },
  jsonRaw: {
    fontFamily: 'monospace',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground1,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    margin: 0,
  },
  // Tree rendering
  treeRoot: {
    fontFamily: 'monospace',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground1,
    lineHeight: '1.55',
  },
  row: {
    display: 'flex',
    alignItems: 'flex-start',
    minHeight: '20px',
  },
  rowIndent: {
    display: 'inline-block',
    flexShrink: 0,
  },
  key: { color: tokens.colorNeutralForeground2 },
  punct: { color: tokens.colorNeutralForeground3 },
  str: { color: tokens.colorPaletteGreenForeground1 },
  num: { color: tokens.colorPaletteDarkOrangeForeground1 },
  bool: { color: tokens.colorPalettePurpleForeground2 },
  nul: { color: tokens.colorNeutralForeground3, fontStyle: 'italic' },
  match: {
    backgroundColor: tokens.colorPaletteYellowBackground2,
    color: tokens.colorNeutralForeground1,
    borderRadius: '2px',
    paddingLeft: '1px',
    paddingRight: '1px',
  },
  matchActive: {
    // Currently-focused match (the one Enter/arrow navigation has landed on).
    // Same padding/border-radius as `match` so layout doesn't shift when
    // navigating. We keep dark foreground for contrast and use a saturated
    // orange tint + 1px ring to clearly distinguish it from inactive matches.
    backgroundColor: tokens.colorPaletteDarkOrangeBackground2,
    color: tokens.colorNeutralForeground1,
    boxShadow: `0 0 0 1px ${tokens.colorPaletteDarkOrangeBorderActive}`,
  },
  matchCount: {
    // Use inline-flex + align-items: center so the counter is properly
    // vertically centered within the Input's contentAfter slot instead of
    // riding the text baseline (which makes "1 of 296" sit visually higher
    // than the query text and the clear-X button).
    display: 'inline-flex',
    alignItems: 'center',
    alignSelf: 'stretch',
    fontSize: tokens.fontSizeBase200,
    lineHeight: 1,
    color: tokens.colorNeutralForeground3,
    fontVariantNumeric: 'tabular-nums',
    paddingRight: tokens.spacingHorizontalSNudge,
    marginRight: tokens.spacingHorizontalXXS,
    borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
    whiteSpace: 'nowrap',
    userSelect: 'none',
  },
  matchCountEmpty: {
    color: tokens.colorPaletteRedForeground1,
  },
  toolbarBtn: {
    minWidth: 'auto',
  },
  empty: {
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
    fontSize: tokens.fontSizeBase200,
    padding: tokens.spacingVerticalS,
  },
});

type Mode = 'formatted' | 'raw';

const INDENT_PX = 16;

type NodeProps = {
  value: unknown;
  depth: number;
  isLast: boolean;
  keyName?: string | undefined;
  query: string;
  styles: ReturnType<typeof useStyles>;
};

const JsonNode = memo(function JsonNode({
  value,
  depth,
  isLast,
  keyName,
  query,
  styles,
}: NodeProps) {
  const isObject = value !== null && typeof value === 'object';
  const isArray = Array.isArray(value);
  const indent = <span className={styles.rowIndent} style={{ width: depth * INDENT_PX }} aria-hidden />;
  const comma = !isLast ? <span className={styles.punct}>,</span> : null;

  const keyLabel = keyName !== undefined ? (
    <>
      <span className={styles.key}>{highlightText(JSON.stringify(keyName), query, styles.match)}</span>
      <span className={styles.punct}>: </span>
    </>
  ) : null;

  if (!isObject) {
    return (
      <div className={styles.row}>
        {indent}
        <span>
          {keyLabel}
          {renderPrimitive(value, styles, query)}
          {comma}
        </span>
      </div>
    );
  }

  const entries: Array<[string, unknown]> = isArray
    ? (value as unknown[]).map((v, i) => [String(i), v])
    : Object.entries(value as Record<string, unknown>);

  const openBrace = isArray ? '[' : '{';
  const closeBrace = isArray ? ']' : '}';
  const isEmpty = entries.length === 0;

  if (isEmpty) {
    return (
      <div className={styles.row}>
        {indent}
        <span>
          {keyLabel}
          <span className={styles.punct}>{openBrace}{closeBrace}</span>
          {comma}
        </span>
      </div>
    );
  }

  return (
    <div>
      <div className={styles.row}>
        {indent}
        <span>
          {keyLabel}
          <span className={styles.punct}>{openBrace}</span>
        </span>
      </div>
      {entries.map(([k, v], i) => (
        <JsonNode
          key={k}
          value={v}
          depth={depth + 1}
          isLast={i === entries.length - 1}
          keyName={isArray ? undefined : k}
          query={query}
          styles={styles}
        />
      ))}
      <div className={styles.row}>
        {indent}
        <span>
          <span className={styles.punct}>{closeBrace}</span>
          {comma}
        </span>
      </div>
    </div>
  );
});

const renderPrimitive = (value: unknown, styles: ReturnType<typeof useStyles>, query: string): React.ReactNode => {
  if (value === null) return <span className={styles.nul}>{highlightText('null', query, styles.match)}</span>;
  if (value === undefined) return <span className={styles.nul}>{highlightText('undefined', query, styles.match)}</span>;
  if (typeof value === 'string') {
    // Use JSON.stringify so quotes, backslashes, newlines, and control
    // characters are properly escaped — keeps the formatted output a
    // faithful representation of the underlying JSON.
    return <span className={styles.str}>{highlightText(JSON.stringify(value), query, styles.match)}</span>;
  }
  if (typeof value === 'number') {
    return <span className={styles.num}>{highlightText(String(value), query, styles.match)}</span>;
  }
  if (typeof value === 'boolean') {
    return <span className={styles.bool}>{highlightText(String(value), query, styles.match)}</span>;
  }
  return <span>{String(value)}</span>;
};

export const JsonViewer = ({ data, label, downloadName = 'data', maxHeight }: JsonViewerProps) => {
  const styles = useStyles();
  const [mode, setMode] = useState<Mode>('formatted');
  const [query, setQuery] = useState('');
  const [copied, setCopied] = useState(false);
  const [activeMatch, setActiveMatch] = useState(0);
  const copyTimeoutRef = useRef<number | null>(null);
  const treeRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => () => {
    if (copyTimeoutRef.current) window.clearTimeout(copyTimeoutRef.current);
  }, []);

  const displayData = useMemo(() => {
    if (typeof data !== 'string') return data;
    const trimmed = data.trim();
    if (!(trimmed.startsWith('{') || trimmed.startsWith('['))) return data;
    try {
      return JSON.parse(trimmed);
    } catch {
      return data;
    }
  }, [data]);

  // Match counter — derived from the data + query. Memoized so we only
  // re-walk when either changes. Used by the input counter, the keyboard
  // navigation, and the active-match highlight effect below.
  const totalMatches = useMemo(() => countMatchesInData(displayData, query), [displayData, query]);

  // Reset the active match whenever the query (or matched count) changes
  // so navigating after editing the query starts from the first hit.
  useEffect(() => {
    setActiveMatch(0);
  }, [query, totalMatches]);

  // After each render, find the active <mark> in the tree, apply the
  // "current match" class to it (clearing it from any previously-active
  // mark), and scroll it into view. Keeps the DOM-based highlight in sync
  // with `activeMatch` without forcing React to re-render every node when
  // the active index changes.
  useEffect(() => {
    const root = treeRef.current;
    if (!root) return;
    // Griffel generates a space-separated class string (atomic classes);
    // `classList.add/remove` require individual tokens, so split first.
    const activeTokens = styles.matchActive.split(/\s+/).filter(Boolean);
    const marks = Array.from(root.querySelectorAll<HTMLElement>('mark'));
    marks.forEach((m) => m.classList.remove(...activeTokens));
    if (totalMatches === 0 || mode !== 'formatted') return;
    const target = marks[activeMatch];
    if (target) {
      target.classList.add(...activeTokens);
      // `scrollIntoView` isn't implemented in jsdom; guard so tests pass.
      target.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
    }
  }, [activeMatch, totalMatches, query, mode, styles.matchActive]);

  const handleSearchKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (totalMatches === 0) return;
    // Enter / ↓ → next match. Shift+Enter / ↑ → previous match.
    // Wrap around at both ends so cycling is intuitive.
    if (e.key === 'Enter' || e.key === 'ArrowDown') {
      e.preventDefault();
      const next = e.shiftKey && e.key === 'Enter'
        ? (activeMatch - 1 + totalMatches) % totalMatches
        : (activeMatch + 1) % totalMatches;
      setActiveMatch(next);
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveMatch((activeMatch - 1 + totalMatches) % totalMatches);
    }
  }, [activeMatch, totalMatches]);

  const jsonString = useMemo(() => {
    try {
      return JSON.stringify(displayData, null, 2);
    } catch {
      return String(displayData);
    }
  }, [displayData]);

  const rawString = useMemo(() => {
    try {
      return JSON.stringify(displayData);
    } catch {
      return String(displayData);
    }
  }, [displayData]);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(mode === 'raw' ? rawString : jsonString);
      setCopied(true);
      if (copyTimeoutRef.current) window.clearTimeout(copyTimeoutRef.current);
      copyTimeoutRef.current = window.setTimeout(() => setCopied(false), 2000);
    } catch {
      console.error('Failed to copy to clipboard');
    }
  }, [mode, rawString, jsonString]);

  const handleDownload = useCallback(() => {
    try {
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${downloadName}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      console.error('Failed to download JSON');
    }
  }, [jsonString, downloadName]);

  const containerStyle = maxHeight ? { maxHeight } : undefined;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        {label && <span className={styles.label}>{label}</span>}
        <div className={styles.toolbar} role="toolbar" aria-label="JSON viewer toolbar">
          {/* Search is only meaningful in formatted mode — the tree
              renderer wires <mark> highlights, active-match navigation,
              and scroll-into-view. Raw mode renders a single <pre> string
              with no highlight wiring, so showing a search input there
              would report "N matches" with no visible highlights and no
              way to jump between them. Hide it instead of pretending to
              support it. */}
          {mode !== 'raw' && (
          <Input
            size="small"
            className={styles.searchInput}
            value={query}
            onChange={(_, d) => setQuery(d.value)}
            onKeyDown={handleSearchKeyDown}
            placeholder="Search keys & values"
            aria-label="Search JSON"
            contentBefore={<SearchRegular />}
            {...(query ? {
              contentAfter: (
                <>
                  {/* Match counter, mirrors VS Code / Chrome find-in-page:
                      "N of M" while there are hits, "No results" otherwise.
                      The trailing border divides it from the clear button so
                      the two sit visually grouped inside the input. */}
                  <span
                    className={mergeClasses(
                      styles.matchCount,
                      totalMatches === 0 && styles.matchCountEmpty,
                    )}
                    role="status"
                    aria-live="polite"
                    aria-atomic="true"
                    title={
                      totalMatches > 0
                        ? 'Enter for next match, Shift+Enter for previous'
                        : undefined
                    }
                  >
                    {totalMatches > 0
                      ? `${activeMatch + 1} of ${totalMatches}`
                      : 'No results'}
                  </span>
                  <Button
                    appearance="transparent"
                    size="small"
                    icon={<DismissRegular />}
                    onClick={() => setQuery('')}
                    aria-label="Clear search"
                  />
                </>
              ),
            } : {})}
          />
          )}
          {/* Right cluster: Formatted/Raw toggle, copy, download.
              marginLeft: auto pushes them to the trailing edge of the
              toolbar to match the HTML mockup layout. */}
          <div className={styles.toolbarRight}>
            <div className={styles.segGroup} role="group" aria-label="Display mode">
              <button
                type="button"
                className={mergeClasses(styles.segBtn, mode === 'formatted' && styles.segBtnActive)}
                onClick={() => setMode('formatted')}
                aria-pressed={mode === 'formatted'}
              >
                Formatted
              </button>
              <button
                type="button"
                className={mergeClasses(styles.segBtn, mode === 'raw' && styles.segBtnActive)}
                onClick={() => setMode('raw')}
                aria-pressed={mode === 'raw'}
              >
                Raw
              </button>
            </div>
            <Tooltip content={copied ? 'Copied!' : 'Copy'} relationship="label">
              <Button
                className={styles.toolbarBtn}
                icon={copied ? <CheckmarkFilled /> : <CopyFilled />}
                size="small"
                appearance="subtle"
                onClick={handleCopy}
                aria-label="Copy JSON to clipboard"
              />
            </Tooltip>
            <Tooltip content="Download .json" relationship="label">
              <Button
                className={styles.toolbarBtn}
                icon={<ArrowDownloadRegular />}
                size="small"
                appearance="subtle"
                onClick={handleDownload}
                aria-label="Download JSON"
              />
            </Tooltip>
          </div>
        </div>
      </div>
      <div className={styles.jsonContainer} style={containerStyle}>
        {mode === 'raw' ? (
          <pre className={styles.jsonRaw}>{rawString}</pre>
        ) : displayData === undefined ? (
          <div className={styles.empty}>No data</div>
        ) : (
          <div className={styles.treeRoot} ref={treeRef}>
            <JsonNode
              value={displayData}
              depth={0}
              isLast
              query={query}
              styles={styles}
            />
          </div>
        )}
      </div>
    </div>
  );
};
